import { useEffect, useRef, useState } from "react";
import { useLiveQuery } from "dexie-react-hooks";
import { ChevronRight } from "lucide-react";
import { budgetStatus, de, monthlyTotals, storeShares } from "@ainkauf/core";
import { BottomNav } from "@/components/BottomNav";
import { allStores, allTrips, currentFamily, ensureLocalFamily, setMonthlyBudget } from "@/db/repository";
import { listenFarbe } from "@/lib/list-color";
import { useNavigate } from "@/lib/router";

const euro = (cents: number) => (cents / 100).toLocaleString("de-DE", { style: "currency", currency: "EUR" });

/**
 * Ausgaben: aktueller Monat gegen das Budget, zwölf Monate als Balken, Anteil
 * je Laden.
 *
 * Bewusst ohne Diagramm-Bibliothek. Zwölf Balken sind ein paar Zeilen CSS, und
 * eine Offline-App soll nicht hundert Kilobyte für etwas laden, das der
 * Browser von selbst kann.
 */
export function SpendingScreen() {
  const go = useNavigate();
  const trips = useLiveQuery(() => allTrips(), [], []);
  const stores = useLiveQuery(() => allStores(), [], []);
  const family = useLiveQuery(() => currentFamily(), [], null);
  // Live-Queries lesen nur, deshalb steht das Anlegen daneben.
  useEffect(() => {
    void ensureLocalFamily();
  }, []);
  const [budgetEingabe, setBudgetEingabe] = useState<string | null>(null);
  const merker = useRef<number | null>(null);

  /**
   * Beim Tippen speichern, nicht erst beim Verlassen des Feldes. Am Telefon
   * verlässt man ein Feld oft gar nicht: man tippt die Zahl und wechselt den
   * Bildschirm. Der halbe Moment Verzögerung sammelt die Tastenanschläge.
   */
  function budgetMerken(wert: string) {
    setBudgetEingabe(wert);
    if (merker.current !== null) window.clearTimeout(merker.current);
    merker.current = window.setTimeout(() => {
      void budgetSchreiben(wert);
    }, 400);
  }

  async function budgetSchreiben(wert: string) {
    if (merker.current !== null) window.clearTimeout(merker.current);
    const zahl = Number.parseFloat(wert.replace(",", "."));
    await setMonthlyBudget(Number.isFinite(zahl) && zahl > 0 ? Math.round(zahl * 100) : null);
  }

  const monat = new Date().toISOString().slice(0, 7);
  const monate = monthlyTotals(trips, 12);
  const budget = budgetStatus(trips, family?.monthly_budget_cents ?? null, monat);
  const anteile = storeShares(trips, monat);
  const hoechster = Math.max(...monate.map((m) => m.totalCents), 1);

  const ladenName = (id: string | null) => stores.find((store) => store.id === id)?.name ?? de.lists.noStore;

  const tonUmBudget =
    budget.level === "drüber" ? "text-accent" : budget.level === "knapp" ? "text-honey" : "text-herb";

  return (
    <div className="mx-auto flex min-h-dvh w-full max-w-2xl flex-col gap-6 px-4 mit-leiste">
      <header className="flex items-center justify-between pt-5">
        <h1 className="text-[2rem]">{de.spending.title}</h1>
        <button
          type="button"
          onClick={() => {
            go("/historie");
          }}
          data-testid="to-history"
          className="inline-flex min-h-[44px] items-center gap-1 rounded-full bg-card px-4 text-sm font-semibold text-ink-2 shadow-[var(--shadow-card)]"
        >
          {de.history.title}
          <ChevronRight size={16} strokeWidth={1.5} aria-hidden />
        </button>
      </header>

      <section
        className="auftritt relative flex flex-col gap-2 overflow-hidden rounded-[var(--radius-xl)] px-5 py-5"
        style={{
          // Dieselben Lichtflecken wie im Kopf der Liste: die Bildschirme
          // sollen erkennbar aus einer Familie stammen.
          background:
            "radial-gradient(120% 90% at 85% -20%, rgb(255 255 255 / 0.7) 0%, transparent 62%), " +
            "linear-gradient(135deg, #fff6ec 0%, var(--honey-soft) 52%, var(--accent-soft) 100%)",
          boxShadow: "var(--shadow-card)",
        }}
      >

        <span className="relative text-[13px] font-bold uppercase tracking-[0.1em] text-ink-2">{de.spending.thisMonth}</span>
        <span className="num relative font-display text-[2.75rem] font-extrabold leading-none tracking-tight text-ink" data-testid="month-total">
          {euro(budget.spentCents)}
        </span>

        {budget.budgetCents !== null && budget.ratio !== null && (
          <>
            <div className="mt-1 h-2.5 overflow-hidden rounded-full bg-paper-2" aria-hidden>
              <div
                className={`h-full rounded-full ${budget.level === "drüber" ? "bg-accent" : budget.level === "knapp" ? "bg-honey" : "bg-herb"}`}
                style={{ width: `${String(Math.min(100, Math.round(budget.ratio * 100)))}%` }}
              />
            </div>
            <span className={`num text-sm ${tonUmBudget}`} data-testid="budget-status">
              {budget.remainingCents !== null && budget.remainingCents >= 0
                ? de.spending.budgetRemaining(euro(budget.remainingCents))
                : de.spending.budgetOver(euro(Math.abs(budget.remainingCents ?? 0)))}
            </span>
          </>
        )}
      </section>

      <section className="flex flex-col gap-3">
        <h2 className="text-lg">{de.spending.perMonth}</h2>
        {trips.length === 0 ? (
          <p className="text-ink-2">{de.spending.noData}</p>
        ) : (
          <ul className="flex flex-col gap-1.5" data-testid="month-bars">
            {monate.map((eintrag, i) => {
              const aktuell = eintrag.month === monat;
              return (
                <li
                  key={eintrag.month}
                  className="auftritt flex items-center gap-3"
                  style={{ ["--takt" as string]: `${String(Math.min(i * 30, 330))}ms` }}
                >
                  <span className={`w-20 shrink-0 text-sm ${aktuell ? "font-bold text-ink" : "text-ink-2"}`}>{eintrag.label}</span>
                  <span className="h-7 flex-1 overflow-hidden rounded-full bg-paper-2">
                    {/* Der laufende Monat in Tomate, die vergangenen in Honig:
                        man sieht sofort, welcher Balken noch wächst. */}
                    <span
                      className="block h-full rounded-full"
                      style={{
                        width: `${String(Math.round((eintrag.totalCents / hoechster) * 100))}%`,
                        background: aktuell
                          ? "linear-gradient(90deg, var(--accent-2), var(--accent))"
                          : "linear-gradient(90deg, var(--food-cream), var(--honey))",
                        transition: "width 520ms var(--ease-out)",
                      }}
                    />
                  </span>
                  <span className={`num w-20 shrink-0 text-right text-sm ${aktuell ? "font-bold text-ink" : "text-ink-2"}`}>
                    {euro(eintrag.totalCents)}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {anteile.length > 0 && (
        <section className="flex flex-col gap-2">
          <h2 className="text-lg">{de.spending.perStore}</h2>
          <ul className="flex flex-col gap-1">
            {anteile.map((anteil, i) => {
              const farbe = listenFarbe(ladenName(anteil.storeId));
              return (
                <li
                  key={anteil.storeId ?? "ohne"}
                  className="karte kante auftritt flex min-h-[54px] items-center justify-between gap-3 pl-4 pr-4"
                  style={{ ["--kante-farbe" as string]: farbe, ["--takt" as string]: `${String(Math.min(i * 40, 240))}ms` }}
                >
                  <span className="truncate font-medium">{ladenName(anteil.storeId)}</span>
                  <span className="num shrink-0 text-sm text-ink-2">
                    {euro(anteil.totalCents)}
                    <span className="ml-2 rounded-full px-2 py-0.5 font-bold" style={{ background: `color-mix(in oklab, ${farbe} 16%, #fff)`, color: `color-mix(in oklab, ${farbe} 70%, var(--ink))` }}>
                      {Math.round(anteil.share * 100)} %
                    </span>
                  </span>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      <section className="flex flex-col gap-2">
        <h2 className="text-lg">{de.spending.budget}</h2>
        <label className="flex flex-col gap-1.5 text-sm text-ink-2">
          {de.spending.budgetHint}
          <input
            value={budgetEingabe ?? (family?.monthly_budget_cents ? String(family.monthly_budget_cents / 100) : "")}
            onChange={(event) => {
              budgetMerken(event.target.value);
            }}
            onBlur={() => {
              if (budgetEingabe !== null) void budgetSchreiben(budgetEingabe);
            }}
            inputMode="decimal"
            placeholder="0,00"
            data-testid="budget-input"
            className="num karte min-h-[52px] px-4 text-ink outline-none"
          />
        </label>
      </section>

      <BottomNav active="ausgaben" />
    </div>
  );
}
